import * as api from 'api';
import { actionTypes } from 'redux-resource';
import { SubmissionError } from 'redux-form';

import { PER_PAGE } from 'helpers/actions';
import { expireTypeToBackendValue } from '../helpers/subs';
import { BIG_PAGE_SIZE } from '../constants';

export const fetchAllTracfoneSubs =
  (page = 1, ordering) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'tracfoneSubs',
      requestKey: `fetchAll${page}`,
      list: `page${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchAllTracfoneSubs(page, ordering)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const fetchAllUserTracfoneSubs = (userId) => async (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    requestKey: `fetchAllUserTracfoneSubs${userId}`,
    list: `userTracfoneSubs${userId}`,
    mergeListIds: false,
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  let subs = [];
  let page = 1;
  let next = 'next-url';

  while (next) {
    let response;

    try {
      response = await api.searchTracfoneSubs(
        page,
        { user_profile: userId },
        BIG_PAGE_SIZE,
      );
    } catch (e) {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
      let messages = e.response
        ? e.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError({
        _error: messages,
      });
    }

    subs = subs.concat(response.data.results);
    next = response.data.next;
    page += 1;
  }

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_SUCCEEDED,
    resources: subs || [],
  });

  return subs;
};

export const fetchUserFilteredSubs =
  (userId, { query, requestKey }, page = 1) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'tracfoneSubs',
      requestKey: `${requestKey}Subs${page}`,
      list: `${requestKey}Subs${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchUserTracfoneFilteredSubs(userId, query, page)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const fetchAllTracfoneInitialsSubs =
  (params, { sort, carriers, expiry_type, status = 'expired' }) =>
  async (dispatch) => {
    const actionDefaults = {
      resourceType: 'tracfoneSubs',
      requestKey: `fetchAllTracfoneInitialsSubs`,
      list: `allTracfoneInitialsSubs`,
      mergeListIds: false,
    };

    const apiCalls = {
      sprint: api.fetchAllInitialsSubs,
      att: api.fetchAllAttInitialsSubs,
      tracfone: api.fetchAllTracfoneInitialsSubs,
      simo: api.fetchAllSimoInitialsSubs,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_SUCCEEDED,
      resources: [],
    });

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    let subs = [];

    for (let carrier of carriers) {
      let page = 1;
      let next = 'next-url';

      while (next) {
        let response;

        try {
          response = await apiCalls[carrier](
            page,
            { ...params, [`${carrier}_status`]: status },
            BIG_PAGE_SIZE,
          );
        } catch (e) {
          let messages = e.response
            ? e.response.data
            : { detail: 'Something went wrong' };
          dispatch({
            ...actionDefaults,
            type: actionTypes.READ_RESOURCES_FAILED,
          });
          throw new SubmissionError({
            _error: messages,
          });
        }

        subs = subs.concat(response.data.results);
        next = response.data.next;
        page += 1;
      }
    }

    // sort subs if needed
    subs = typeof sort === 'function' ? sort(subs) : subs;

    // filter out subs with wrong expiry type
    subs = subs.filter(
      (sub) =>
        sub.tracfone_status !== 'expired' ||
        sub.expiry_type === expireTypeToBackendValue(expiry_type),
    );

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_SUCCEEDED,
      resources: subs,
    });

    return subs;
  };

export const updatePlan = (subId, plan) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    requestKey: 'updatePlan',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .updateTracfonePlan(subId, plan)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (err.response && err.response.status) {
        throw new SubmissionError({ messages, status: err.response.status });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const createSub = (attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    requestKey: 'create',
    list: 'userSubs',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.CREATE_RESOURCES_PENDING,
  });

  return api
    .createTracfoneSub(attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.CREATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.CREATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const updateTracfoneSub = (id, sub) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    requestKey: 'updateTracfoneSub',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .updateTracfoneSub(id, sub)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const updateDevice = (id, device) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    requestKey: 'updateDevice',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .updateTracfoneDevice(id, device)
    .then((response) => {
      if (response.data.valid === false) {
        throw new Error(response);
      }
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [id],
        requestProperties: {
          details: {
            valid: response.data.valid ? response.data.valid : true,
            ...response.data.details,
          },
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const updatePort = (subId, port) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    requestKey: `updatePort${subId}`,
    resources: [subId],
  };

  const old_carrier_account_number = port.old_carrier_account_number
    ? port.old_carrier_account_number.replace(/\D+/gi, '')
    : port.old_carrier_account_number;
  const mdn = port.mdn ? port.mdn.replace(/\D+/gi, '') : port.mdn;

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .updateTracfonePort(subId, { ...port, mdn, old_carrier_account_number })
    .then((response) => {
      const { subscription, ...requestProperties } = response.data;
      const subMeta = {};
      subMeta[subId] = {
        portInfo: requestProperties.port_info,
      };
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        requestProperties,
      });
      dispatch({
        type: 'UPDATE_RESOURCES',
        meta: {
          tracfoneSubs: subMeta,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
        requestProperties: err.response && err.response.data,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      let errObj =
        messages.detail || messages.message || messages.non_field_errors;
      if (errObj) {
        throw new SubmissionError({ _error: errObj });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const submitPort = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    requestKey: 'submitPort',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .submitTracfonePort(id)
    .then((response) => {
      if (response.data.valid === false) {
        throw new Error(response);
      }

      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [id],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const portCancel = (subId, cancel) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    resources: [subId],
    requestKey: 'portCancel',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .cancelTracfonePort(subId, cancel)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data.subscription],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const portStatus = (id, data) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    requestKey: 'portStatus',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .tracfonePortStatus(id, data)
    .then((response) => {
      if (response.data.valid === false) {
        throw new Error(response);
      }

      const { subscription, ...portStatus } = response.data;

      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [id],
        meta: {
          portStatus,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail) {
        throw new SubmissionError({ _error: messages });
      } else if (messages.message) {
        throw new SubmissionError({ _error: messages.message });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const portInfo = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    resources: [id],
    requestKey: `portInfo${id}`,
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .tracfonePortInfo(id)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        requestProperties: {
          data: response.data.current,
        },
        meta: {
          portInfo: response.data.port_info,
          allPortInfo: response.data,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
    });
};

export const activateSub = (subId, activation) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    requestKey: 'activateSub',
    resources: [subId],
  };

  const mdn = activation.mdn
    ? activation.mdn.replace(/\D+/gi, '')
    : activation.mdn;

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .activateTracfoneSub(subId, { ...activation, mdn })
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        meta: {
          activation: {
            ...response.data,
            pin: activation.pin,
          },
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const findTracfoneSub = (subId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    requestKey: 'find',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .findTracfoneSub(subId)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
        statusCode: err.response && err.response.status,
      });
    });
};

export const fetchUserSubs =
  (userId, page = 1) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'tracfoneSubs',
      requestKey: `fetchTracfoneByUser${page}`,
      list: 'userSubs',
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchUserTracfoneSubs(userId, page)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const searchSubs =
  (page = 1, search) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'tracfoneSubs',
      requestKey: `searchSubs${page}`,
      list: `searchPage${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .searchTracfoneSubs(page, search)
      .then((response) => {
        const { count, next, previous, results } = response.data;

        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const expireTracfoneSubscription = (subId, values) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    resources: [subId],
    requestKey: 'expireSub',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .expireTracfoneSubscription(subId, values)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data.subscription],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const findTracfoneSubDataUsage = (subId, cycle) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    requestKey: 'dataUsage',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .findTracfoneSubDataUsage(subId, cycle)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        meta: {
          dataUsage: response.data.details,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
    });
};

export const updateStatus = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    requestKey: 'updateStatus',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .updateTracfoneStatus(id)
    .then((response) => {
      if (response.data.valid === false) {
        throw new Error(response);
      }

      const { subscription, ...updateStatus } = response.data;

      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [id],
        meta: {
          updateStatus,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const updateMdn = (subId, mdn) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'tracfoneSubs',
    resources: [subId],
    requestKey: 'updateMdn',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .updateTracfoneMdn(subId, { mdn })
    .then((response) => {
      const { message, ...sub } = response.data;
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [sub],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail || messages.error) {
        throw new SubmissionError({ _error: messages });
      } else {
        throw new SubmissionError(messages);
      }
    });
};
